// vg-binding: P7.D-148
// vg-binding: P18.D-21
// vg-binding: P18.D-28
//
// apps/vendor/src/features/orders/detail/TransitionTimeline.tsx
//
// Renders the OrderItemTransition activity log returned by Task 15.
//
// Phase 18 Task 34 — the actor is a LABEL now, not a raw enum.
//
// The row used to print `transitioned_by_role` verbatim, so a vendor reading
// the log saw `vendor_staff` / `admin_override` in every locale. Phase 18 adds
// a fourth actor — `vendor_api`, the vendor's OWN system writing through the
// machine lane — and printing that one raw would be worse than untranslated:
// it is the row where the reader most needs to understand that no colleague did
// this. P18.D-01 forbids naming a person for a machine action, so the machine
// row carries `transitioned_by_staff_id: null` and is badged as such instead of
// borrowing a name, showing a blank, or being folded into `system` (which means
// the PLATFORM acted — a different claim about who is accountable).
//
// Covers goal: G-27

import { VendorStatusBadge } from '@printway/ui';

import { useTranslation } from '../../../lib/i18n/index.js';
import type { TransitionActorRole, TransitionRow } from '../api.js';

function formatTimestamp(ms: number): string {
  return new Date(ms).toLocaleString(undefined);
}

/**
 * Role -> i18n key. A map rather than a template string so an unrecognised
 * value (a lane added on the server before this bundle ships) renders the
 * explicit "unknown actor" copy instead of leaking `transitionTimeline.actor.
 * <whatever>` into the page.
 */
const ACTOR_LABEL_KEY: Record<TransitionActorRole, string> = {
  vendor_staff: 'transitionTimeline.actor.vendor_staff',
  admin_override: 'transitionTimeline.actor.admin_override',
  system: 'transitionTimeline.actor.system',
  vendor_api: 'transitionTimeline.actor.vendor_api',
};

/** A row written by the vendor's own system through the machine lane. */
export function isMachineTransition(row: TransitionRow): boolean {
  return row.transitioned_by_role === 'vendor_api';
}

interface Props {
  rows: TransitionRow[];
  isLoading: boolean;
}

export function TransitionTimeline({ rows, isLoading }: Props): JSX.Element {
  const { t } = useTranslation('orders');
  if (isLoading) {
    return (
      <section className="h-40 animate-pulse rounded-xl border border-pw-border bg-pw-surface" />
    );
  }
  return (
    <section
      data-testid="order-detail-transitions"
      className="rounded-xl border border-pw-border bg-pw-bg p-5 shadow-sm"
    >
      <h3 className="mb-3 text-sm font-semibold text-pw-text">{t('transitionTimeline.title')}</h3>
      {rows.length === 0 ? (
        <p className="rounded-md border border-dashed border-pw-border bg-pw-surface px-3 py-4 text-center text-sm text-pw-text-muted">
          {t('transitionTimeline.empty')}
        </p>
      ) : (
        <ol className="flex flex-col gap-3">
          {rows.map((r) => {
            const labelKey = ACTOR_LABEL_KEY[r.transitioned_by_role] as
              | string
              | undefined;
            const actorLabel = labelKey
              ? t(labelKey)
              : t('transitionTimeline.actor.unknown');
            const machine = isMachineTransition(r);
            return (
              <li
                key={r.event_id}
                className="flex gap-3 border-l-2 border-pw-primary/40 pl-3"
                data-actor-role={r.transitioned_by_role}
                data-testid={`order-transition-${r.event_id}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-pw-text">
                    {r.from_state} → {r.to_state}
                    <span className="ml-2 font-mono text-xs text-pw-text-muted">
                      v{r.from_version} → v{r.to_version}
                    </span>
                  </p>
                  <p className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-pw-text-muted">
                    <span>
                      {formatTimestamp(r.occurred_at)} · {actorLabel}
                    </span>
                    {machine ? (
                      <VendorStatusBadge
                        status="external_system"
                        label={t('transitionTimeline.machineBadge')}
                        testId={`order-transition-machine-${r.event_id}`}
                      />
                    ) : null}
                  </p>
                  {r.reason ? (
                    <p className="mt-1 text-xs italic text-pw-text">
                      {t('transitionTimeline.labelReason', { reason: r.reason })}
                    </p>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}

export default TransitionTimeline;
